import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { ThemeClasses } from "../types";
import { getCustomSelectStyles, FilterPopover } from "../../../../shared";

interface GeofenceFiltersProps {
  selectedShape: string;
  selectedType: string;
  selectedStatus: string;
  searchQuery: string;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onShapeChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function GeofenceFilters({
  selectedShape,
  selectedType,
  selectedStatus,
  searchQuery,
  isDarkMode,
  themeClasses,
  onShapeChange,
  onTypeChange,
  onStatusChange,
  onSearchChange,
}: GeofenceFiltersProps) {
  const { t } = useTranslation("adminSections");

  const shapeOptions = [
    { value: "all", label: t("geofences.filters.allShapes", "Todas las formas") },
    { value: "circular", label: t("geofences.shapes.circular", "Circulares") },
    { value: "linear", label: t("geofences.shapes.linear", "Lineales") },
    { value: "polygonal", label: t("geofences.shapes.polygonal", "Poligonales") },
  ];

  const typeOptions = [
    { value: "all", label: t("geofences.filters.allTypes", "Todos los tipos") },
    { value: "warehouse", label: t("geofences.types.warehouse", "Almacén") },
    { value: "restrictedArea", label: t("geofences.types.restrictedArea", "Zona restringida") },
    { value: "customer", label: t("geofences.types.customer", "Cliente") },
    { value: "route", label: t("geofences.types.route", "Ruta") },
    { value: "maintenance", label: t("geofences.types.maintenance", "Mantenimiento") },
    { value: "authorizedStop", label: t("geofences.types.authorizedStop", "Parada autorizada") },
    { value: "truck_yard", label: t("geofences.types.truck_yard", "Patio de Maniobras") },
  ];

  const statusOptions = [
    { value: "all", label: t("geofences.filters.allStatuses", "Todos los estados") },
    { value: "active", label: t("geofences.statuses.active", "Activa") },
    { value: "inactive", label: t("geofences.statuses.inactive", "Inactiva") },
  ];

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {/* Filtro por búsqueda */}
      <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
        <i
          className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
        ></i>
        <input
          type="text"
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t("geofences.filters.searchPlaceholder", "Buscar geocercas...")}
          className={`h-9 pl-10 pr-3 py-2 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          } border ${themeClasses.cardBorder} rounded-lg ${
            themeClasses.text
          } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
        />
      </div>

      <FilterPopover
        isDarkMode={isDarkMode}
        activeCount={
          (selectedShape !== "all" ? 1 : 0) +
          (selectedType !== "all" ? 1 : 0) +
          (selectedStatus !== "all" ? 1 : 0)
        }
        onClear={() => {
          onShapeChange("all");
          onTypeChange("all");
          onStatusChange("all");
        }}
      >
        <Select
          value={shapeOptions.find((opt) => opt.value === selectedShape)}
          onChange={(option: any) => onShapeChange(option?.value || "all")}
          options={shapeOptions}
          styles={getCustomSelectStyles(isDarkMode)}
          isSearchable={false}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />

        {/* Filtro por tipo */}
        <Select
          value={typeOptions.find((opt) => opt.value === selectedType)}
          onChange={(option: any) => onTypeChange(option?.value || "all")}
          options={typeOptions}
          styles={getCustomSelectStyles(isDarkMode)}
          isSearchable={false}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />

        {/* Filtro por estado */}
        <Select
          value={statusOptions.find((opt) => opt.value === selectedStatus)}
          onChange={(option: any) => onStatusChange(option?.value || "all")}
          options={statusOptions}
          styles={getCustomSelectStyles(isDarkMode)}
          isSearchable={false}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </FilterPopover>
    </div>
  );
}
